import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AxiosError } from "axios";
import { isInLoggedInUsersTenancy, userHasPermission } from "../../../../swzutils/UserPermissionsUtil";
import { Modal, Spin, Tabs } from "antd";
import { axiosErrorHandler, isConcurrentModificationError, showErrorNotification } from "../../../../swzutils";
import PartnerVehicleForm, {} from "../../components/PartnerVehicleForm";
import FleetVehicleForm, {} from "../../components/FleetVehicleForm";
import RelationshipType from "../../enums/RelationshipType";
import { ConsignmentUtils } from "./api/ConsignmentUtils";
import ConsignmentResource from "./api/ConsignmentResource";
import ConsignmentAssignedVehicleResource from "./api/ConsignmentAssignedVehicleResource";
import { useAppSelector } from "../../../../store";
import { loggedInUserSelector, selectedTenantSelector } from "../../../../store/slice/auth";
export var ConsignmentAssignVehicleModalTabKey;
(function (ConsignmentAssignVehicleModalTabKey) {
    ConsignmentAssignVehicleModalTabKey["FLEET"] = "FLEET";
    ConsignmentAssignVehicleModalTabKey["PARTNER"] = "PARTNER";
})(ConsignmentAssignVehicleModalTabKey || (ConsignmentAssignVehicleModalTabKey = {}));
const ConsignmentAssignVehicleModal = ({ open, consignment, onAssigned, onCancel }) => {
    const [networkRequestInProgress, setNetworkRequestInProgress] = useState(false);
    const [activeTab, setActiveTab] = useState(ConsignmentAssignVehicleModalTabKey.FLEET);
    const fleetVehicleFormRef = useRef(null);
    const partnerVehicleFormRef = useRef(null);
    const selectedTenant = useAppSelector(selectedTenantSelector);
    const loggedInUser = useAppSelector(loggedInUserSelector);
    useEffect(() => {
        if (open) {
            fleetVehicleFormRef.current?.resetFields();
            partnerVehicleFormRef.current?.resetFields();
        }
    }, [open]);
    const onTabChanged = (activeKey) => {
        setActiveTab(activeKey);
    };
    const validateAndAssign = () => {
        if (ConsignmentAssignVehicleModalTabKey.FLEET === activeTab) {
            fleetVehicleFormRef.current?.validateFields().then(assignToFleetOrPartner);
        }
        else {
            partnerVehicleFormRef.current?.validateFields().then(assignToFleetOrPartner);
        }
    };
    const assignToFleetOrPartner = (formValues) => {
        setNetworkRequestInProgress(true);
        const isOnMarketplace = ConsignmentUtils.isOnMarketplace(consignment);
        if (isOnMarketplace || activeTab == ConsignmentAssignVehicleModalTabKey.FLEET) {
            assignToVehicleId(consignment, formValues.vehicleId);
        }
        else {
            appointCarrierToConsignment(consignment, formValues);
        }
    };
    const appointCarrierToConsignment = (consignment, formValues) => {
        ConsignmentResource.appointCarrier(consignment.id, consignment.version, formValues.partnerTenantId, response => assignToVehicleId(response, formValues.vehicleId), error => checkForConcurrentError(error, "The delivery could not be appointed to the carrier"), () => { });
    };
    const assignToVehicleId = (consignment, vehicleId) => {
        const request = {
            consignmentVersion: consignment.version,
            workDate: null, /* defaults to delivery date */
            vehicleId
        };
        ConsignmentAssignedVehicleResource.assignVehicleToConsignment(consignment.id, request, onAssigned, error => checkForConcurrentError(error, "The vehicle assignment could not be performed"), () => setNetworkRequestInProgress(false));
    };
    const checkForConcurrentError = (error, actionDescription) => {
        if (isConcurrentModificationError(error)) {
            showErrorNotification(`${actionDescription}. This is because another user has modified the same delivery.  Please refresh your page and try again.`);
        }
        else {
            axiosErrorHandler(error);
        }
        setNetworkRequestInProgress(false);
    };
    const buildTabItems = (consignment) => {
        const isOnMarketplace = ConsignmentUtils.isOnMarketplace(consignment);
        const isOwnedConsignment = isInLoggedInUsersTenancy(loggedInUser, selectedTenant?.id, consignment.tenantId);
        const partnerTenantId = consignment.carrier?.id;
        // If this consignment is on marketplace then it can only be assigned to a contractor
        const partnerRelationship = isOnMarketplace ? RelationshipType.CONTRACTOR : RelationshipType.CARRIER;
        const partnerTabTitle = partnerRelationship === RelationshipType.CARRIER ? "Carrier Partner" : "Contractor Partner";
        const canAssignToPartner = userHasPermission(loggedInUser, [
            'admin:consignmentVehicle:assignToCarrier',
            'admin:consignmentVehicle:assignToContractor',
            'admin:consignmentMarketplace:assignToContractor'
        ]);
        const canAssignToFleet = userHasPermission(loggedInUser, ['admin:consignmentVehicle:assignToFleet']);
        let defaultActiveTab = ConsignmentAssignVehicleModalTabKey.FLEET;
        let tabItems = [];
        if ((isOwnedConsignment || isOnMarketplace) && canAssignToFleet) {
            tabItems.push({
                key: ConsignmentAssignVehicleModalTabKey.FLEET,
                label: 'My Vehicles',
                children: _jsx(FleetVehicleForm, { tenantId: isOwnedConsignment ? consignment?.tenantId : undefined, ref: fleetVehicleFormRef })
            });
        }
        else {
            defaultActiveTab = ConsignmentAssignVehicleModalTabKey.PARTNER;
        }
        if (canAssignToPartner) {
            tabItems.push({
                key: ConsignmentAssignVehicleModalTabKey.PARTNER,
                label: partnerTabTitle,
                children: _jsx(PartnerVehicleForm, { sourceTenantId: consignment.tenantId, partnerTenantId: partnerTenantId, partnerRelationship: partnerRelationship, ref: partnerVehicleFormRef })
            });
        }
        setActiveTab(defaultActiveTab);
        return tabItems;
    };
    const tabItems = useMemo(() => {
        return consignment ? buildTabItems(consignment) : [];
    }, [loggedInUser, selectedTenant?.id, consignment]);
    return (_jsx(Modal, { title: "Assign Delivery to Vehicle", open: open, onOk: validateAndAssign, onCancel: onCancel, confirmLoading: networkRequestInProgress, destroyOnHidden: true, children: _jsx(Spin, { spinning: networkRequestInProgress, children: _jsx(Tabs, { activeKey: activeTab, onChange: onTabChanged, items: tabItems }) }) }));
};
export default ConsignmentAssignVehicleModal;
